import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import ProgrammeEntryCardActions from './programme_entry_card_actions';
import ProgrammeMealCardContent from './programme_meal_card_content';
import { showGlobalSnackbar } from '../../features/global_snackbar/global_snackbar_slice';
import { parentIDOfMealID } from '../../services/smorg_board';
import { useGetOwnedRecipesBoardsQuery } from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import {
  copyProgrammePlanEntryToProgramme,
  programmePlanEntryQuickDuplicate,
} from '../../operations/programmes_operations';
import {
  useCreateContentEntryMutation,
  useGetDistilledMealsByParentsQuery,
  useGetProgrammesQuery,
  useGetSpaceQuery,
  useLazyGetContentEntryQuery,
  useLazyGetProgrammeQuery,
  useUpdateProgrammeMutation,
} from '../../app/smorgAppSyncGraphQLApi';
import { planEntryByEntryID } from '../../services/programmes';
import { healthProGroupForSpaceID } from '../../services/users';
import { reportAccountActivitySignalOperation } from '../../operations/user_profile_operations';
import { ActivityObjectType } from '../../API';
import { userFriendlyNameForActivitySignalSelector } from '../../features/user_id/user_id_slice';

const ProgrammeEntryMealCard = ({
  id,
  appBoardId: programmePlanId,
  title,
  style,
  className,
  onClick,
  onDelete,
  // isPreview,
  preferredMetricsWithRules,
  targetNutrition,
  targetCalories,
}) => {
  const dispatch = useDispatch();
  const mealID = style._objectID;

  const { data: ownedRecipesBoards } = useGetOwnedRecipesBoardsQuery();

  const { data: ownedProgrammesUnsorted } = useGetProgrammesQuery();

  const programme = (ownedProgrammesUnsorted || []).find((pr) =>
    pr.plans.some((pl) => pl.id === programmePlanId),
  );

  const plan = programme.plans.find((pl) => pl.id === programmePlanId);

  const entry = planEntryByEntryID(plan, id);

  const { data: healthProSpace } = useGetSpaceQuery();

  const currentHealthProGroup =
    healthProSpace && healthProGroupForSpaceID(healthProSpace.id);

  const [lazyGetProgramme] = useLazyGetProgrammeQuery();

  const [updateProgramme] = useUpdateProgrammeMutation();

  const [lazyGetContentEntry] = useLazyGetContentEntryQuery();

  const [createContentEntry] = useCreateContentEntryMutation();

  const parentID = parentIDOfMealID(mealID, ownedRecipesBoards);

  const { data: distilledMealsByParents } = useGetDistilledMealsByParentsQuery(
    programme?.recipesBoardIDs || [],
    {
      skip: (programme?.recipesBoardIDs || []).length === 0,
    },
  );

  const distilledMealsByParent = (distilledMealsByParents || {})[parentID];

  const distilledMeal = (distilledMealsByParent || {})[mealID] || null;

  const friendlyUsername = useSelector(
    userFriendlyNameForActivitySignalSelector,
  );

  const onQuickDuplicate = async () => {
    reportAccountActivitySignalOperation({
      spaceID: healthProSpace.id,
      friendlyUsername,
      lastActivityObjectType: ActivityObjectType.PROGRAMMES,
      lastActivityObjectID: programme.id,
    });
    return programmePlanEntryQuickDuplicate(
      programme,
      programmePlanId,
      entry,
      currentHealthProGroup,
      updateProgramme,
      lazyGetContentEntry,
      createContentEntry,
    );
  };

  if (!distilledMeal) {
    return null;
  }

  const onCopyToProgramme = async (toProgrammeId, toPlanId, dayIndexes) => {
    reportAccountActivitySignalOperation({
      spaceID: healthProSpace.id,
      friendlyUsername,
      lastActivityObjectType: ActivityObjectType.PROGRAMMES,
      lastActivityObjectID: programme.id,
    });
    return copyProgrammePlanEntryToProgramme(
      entry,
      toProgrammeId,
      toPlanId,
      dayIndexes,
      lazyGetProgramme,
      updateProgramme,
    );
  };

  const setGlobalSnackbar = (obj) => dispatch(showGlobalSnackbar(obj));

  const isPreview = false;

  return (
    <ProgrammeMealCardContent
      id={id}
      title={title}
      imageUrl={distilledMeal.recipes[0].imageUrl}
      mealTypes={distilledMeal.recipes[0].mealTypes}
      totalNutritionPerServing={
        distilledMeal.derivedNutrition?.totalNutritionPerServing
      }
      preferredMetricsWithRules={preferredMetricsWithRules}
      targetNutrition={targetNutrition}
      targetCalories={targetCalories}
      onClick={onClick}
      style={style}
      className={className}
      actions={
        !isPreview && (
          <ProgrammeEntryCardActions
            programmeId={programme.id}
            onQuickDuplicate={onQuickDuplicate}
            onCopyToProgramme={(programmeId, planId, dayIndexes) => {
              onCopyToProgramme(programmeId, planId, dayIndexes);
              setGlobalSnackbar({
                notificationText: 'Your meal has been copied to your program',
                linkText: 'View program',
                linkTarget: `/programmes/${programmeId}/plans/${planId}`,
              });
            }}
            onDelete={() => onDelete(id)}
          />
        )
      }
    />
  );
};

ProgrammeEntryMealCard.propTypes = {
  id: PropTypes.string.isRequired,
  appBoardId: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  style: PropTypes.object.isRequired,
  className: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  // isPreview: PropTypes.bool,
  preferredMetricsWithRules: PropTypes.arrayOf(
    PropTypes.shape({
      metricName: PropTypes.string.isRequired,
    }),
  ),
  targetNutrition: PropTypes.objectOf(
    PropTypes.shape({
      targetQuantity: PropTypes.number,
      constraints: PropTypes.objectOf(PropTypes.number),
    }),
  ).isRequired,
  targetCalories: PropTypes.number.isRequired,
};

ProgrammeEntryMealCard.defaultProps = {
  // isPreview: false,
  preferredMetricsWithRules: [],
};

export default ProgrammeEntryMealCard;
